import * as React from 'react';
import { cn } from '@/lib/utils';

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
}

const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  ({ className, variant = 'default', width, height, style, ...props }, ref) => {
    const baseClasses = 'animate-pulse bg-gray-200 dark:bg-gray-800';
    
    const variantClasses = {
      default: 'rounded-md',
      circular: 'rounded-full',
      rectangular: 'rounded-none',
    };

    return (
      <div
        ref={ref}
        className={cn(baseClasses, variantClasses[variant], className)}
        style={{
          width,
          height,
          ...style,
        }}
        {...props}
      />
    );
  }
);
Skeleton.displayName = 'Skeleton';

// Text Skeleton
const SkeletonText = ({ lines = 3, className }: { lines?: number; className?: string }) => (
  <div className={cn('space-y-2', className)}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton
        key={i}
        className="h-4"
        style={{ width: i === lines - 1 ? '70%' : '100%' }}
      />
    ))}
  </div>
);

// Card Skeleton
const SkeletonCard = ({ className }: { className?: string }) => (
  <div className={cn('rounded-xl border border-gray-200 p-6 dark:border-gray-800', className)}>
    <div className="flex items-center gap-4 mb-4">
      <Skeleton variant="circular" width={48} height={48} />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    </div>
    <SkeletonText lines={3} />
  </div>
);

// Table Row Skeleton
const SkeletonTableRow = ({ cols = 5, className }: { cols?: number; className?: string }) => (
  <tr className={cn('border-b border-gray-100 dark:border-gray-800', className)}>
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <Skeleton className="h-4" style={{ width: i === 0 ? '60%' : '80%' }} />
      </td>
    ))}
  </tr>
);

// Loading Spinner
interface SpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: 'sm' | 'md' | 'lg';
}

const spinnerSizes = {
  sm: 'h-4 w-4 border-2',
  md: 'h-6 w-6 border-2',
  lg: 'h-8 w-8 border-3',
};

const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
  ({ className, size = 'md', ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'animate-spin rounded-full border-gray-200 border-t-brand-500 dark:border-gray-700 dark:border-t-brand-400',
        spinnerSizes[size],
        className
      )}
      {...props}
    />
  )
);
Spinner.displayName = 'Spinner';

// Full Page Loading
const LoadingOverlay = ({ message = 'Đang tải...' }: { message?: string }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm dark:bg-gray-900/80">
    <div className="flex flex-col items-center gap-4">
      <Spinner size="lg" />
      <p className="text-sm text-gray-500 dark:text-gray-400">{message}</p>
    </div>
  </div>
);

// Inline Loading
const LoadingInline = ({ message }: { message?: string }) => (
  <div className="flex items-center justify-center gap-2 py-8">
    <Spinner size="md" />
    {message && <span className="text-sm text-gray-500 dark:text-gray-400">{message}</span>}
  </div>
);

export {
  Skeleton,
  SkeletonText,
  SkeletonCard,
  SkeletonTableRow,
  Spinner,
  LoadingOverlay,
  LoadingInline,
};
